import { Fragment, useEffect, useRef, useState } from 'react';
import { Activity, ChevronDown, Globe, ShieldCheck, X } from 'lucide-react';
import { apiFetch } from '../../lib/api';
import { fetchAuditLog, type AuditEvent } from '../../lib/audit-api';
import { formatDateTime } from '../../lib/datetime';
import { Button, Badge } from '../../components/ui';
import styles from './admin.module.css';

interface SiteLite {
  id: string;
  name: string;
}

/** "contact.merge" → "Contact merge". */
function actionLabel(action: string): string {
  const text = action.replace(/[._]/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// Who did what to whom: deletions, merges, agent and role changes, site
// tokens and access rules, webhooks, system settings, routing. An
// account-admin sees their own account's trail; the platform operator, all.
export function AdminAudit() {
  const [sites, setSites] = useState<SiteLite[]>([]);
  const [siteId, setSiteId] = useState('');
  const [action, setAction] = useState('');
  const [actions, setActions] = useState<string[]>([]);
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const req = useRef(0);

  useEffect(() => {
    apiFetch<{ sites: SiteLite[] }>('/api/admin/sites')
      .then((r) => setSites(r.sites))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const id = ++req.current;
    setLoading(true);
    setError(null);
    fetchAuditLog({ siteId, action })
      .then((page) => {
        if (id !== req.current) return;
        setEvents(page.events);
        setCursor(page.nextCursor);
        setActions(page.actions);
      })
      .catch(() => {
        if (id === req.current) setError('Couldn’t load the audit log.');
      })
      .finally(() => {
        if (id === req.current) setLoading(false);
      });
  }, [siteId, action]);

  async function loadOlder() {
    if (!cursor) return;
    const id = ++req.current;
    setLoading(true);
    try {
      const page = await fetchAuditLog({ siteId, action, cursor });
      if (id !== req.current) return;
      setEvents((prev) => [...prev, ...page.events]);
      setCursor(page.nextCursor);
    } catch {
      if (id === req.current) setError('Couldn’t load older entries.');
    } finally {
      if (id === req.current) setLoading(false);
    }
  }

  return (
    <div>
      <div className={styles.headerRow}>
        <div className={styles.headerTitles}>
          <div className={styles.kicker}>
            <ShieldCheck size={13} />
            <span>Workspace Scope</span>
          </div>
          <h1 className={styles.title}>Audit Log</h1>
          <p className={styles.subtitle}>
            Security and administration event log: contact merges, credentials, roles, and settings changes.
          </p>
        </div>
      </div>

      {error && <p className={styles.error} style={{ marginBottom: 16 }} role="alert">{error}</p>}

      {/* Filters: one row on desktop, wrapping on narrow screens. */}
      <div className={styles.filterBar} role="group" aria-label="Filter audit events">
        <label className={`${styles.filterControl} ${siteId ? styles.filterControlActive : ''}`}>
          <Globe size={15} className={styles.filterIcon} aria-hidden="true" />
          <span className={styles.filterPrefix}>Site</span>
          <select
            className={styles.filterSelect}
            value={siteId}
            onChange={(e) => setSiteId(e.target.value)}
            aria-label="Site"
          >
            <option value="">All sites</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <ChevronDown size={14} className={styles.filterChevron} aria-hidden="true" />
        </label>
        <label className={`${styles.filterControl} ${action ? styles.filterControlActive : ''}`}>
          <Activity size={15} className={styles.filterIcon} aria-hidden="true" />
          <span className={styles.filterPrefix}>Action</span>
          <select
            className={styles.filterSelect}
            value={action}
            onChange={(e) => setAction(e.target.value)}
            aria-label="Action"
          >
            <option value="">All actions</option>
            {actions.map((a) => <option key={a} value={a}>{actionLabel(a)}</option>)}
          </select>
          <ChevronDown size={14} className={styles.filterChevron} aria-hidden="true" />
        </label>
        {(siteId || action) && (
          <button
            type="button"
            className={styles.filterClear}
            onClick={() => {
              setSiteId('');
              setAction('');
            }}
          >
            <X size={14} aria-hidden="true" />
            Clear filters
          </button>
        )}
        <span className={styles.filterCount} aria-live="polite">
          {loading ? 'Loading…' : `${events.length}${cursor ? '+' : ''} ${events.length === 1 ? 'event' : 'events'}`}
        </span>
      </div>

      <section className={styles.section} style={{ padding: 0, border: 'none', background: 'transparent' }}>
        {loading && events.length === 0 ? (
          <p style={{ color: 'var(--inic-ink-muted)', fontSize: 13 }}>Loading audit log…</p>
        ) : events.length === 0 ? (
          <div className={styles.emptyState}>
            <ShieldCheck size={32} className={styles.emptyIcon} />
            <div className={styles.emptyTitle}>No matching audit events</div>
            <div className={styles.emptyDescription}>No events match the selected filter criteria.</div>
          </div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr><th>When</th><th>Who</th><th>Action</th><th>What</th><th>Site</th><th style={{ textAlign: 'right' }}>Details</th></tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <Fragment key={e.id}>
                    <tr>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(e.createdAt)}</td>
                      <td>{e.actorLabel}</td>
                      <td>
                        <Badge variant="outline" className="text-[11px] font-normal text-muted-foreground">
                          {actionLabel(e.action)}
                        </Badge>
                      </td>
                      <td>{e.summary}</td>
                      <td>{e.siteName ?? '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        {Object.keys(e.details).length > 0 || e.ip ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs px-2 text-muted-foreground hover:text-foreground"
                            onClick={() => setOpen((o) => (o === e.id ? null : e.id))}
                          >
                            {open === e.id ? 'Hide' : 'Details'}
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                    {open === e.id ? (
                      <tr>
                        <td colSpan={6} style={{ background: 'var(--inic-paper-recessed)' }}>
                          <pre className={styles.mono} style={{ margin: 0, padding: 8, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                            {JSON.stringify({ ...e.details, ...(e.ip ? { ip: e.ip } : {}) }, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {cursor ? (
          <div style={{ marginTop: 16 }}>
            <Button
              variant="outline"
              size="sm"
              disabled={loading}
              onClick={() => void loadOlder()}
              className="h-9 px-4 text-xs font-semibold"
            >
              {loading ? 'Loading…' : 'Load Older Events'}
            </Button>
          </div>
        ) : null}
      </section>
    </div>
  );
}
